// @ts-check
import { forEach, get, isNil } from "lodash";

import { createMacroAllocationId } from "src/allocation/util/macroAllocation";
import { roundFte } from "src/util/roundingStrategy";

import { SUCCESS } from "../saveStatus";

import {
  newLineItem,
  setLineItemWithForecastDefaultCells,
  hashLineItem,
  getDailyRateByFlag,
  setGroupingWithCalculatedSumTotals,
} from "./util";
import { setGroupingTotalFteSum } from "./updateIntersectingFteSumForDateRanges";
import { shouldHideForecastGroup } from "./util/shouldHideForecastGroup";

/**
 * @param {{
 *   allocationProjectId: string,
 *   sourceGroupId: string,
 *   targetGroupId: string,
 *   intersectingFte: number,
 * }[]} rows
 * @param {Record<string, unknown>} cellLookup
 */
export const filterIntersectionOnlyRows = (rows, cellLookup) =>
  (rows || []).filter((row) => {
    if (!row || !(row.intersectingFte > 0)) {
      return false;
    }
    const cellId = createMacroAllocationId(
      row.allocationProjectId,
      row.sourceGroupId,
      row.targetGroupId
    );
    const existingCell = /** @type {any} */ (cellLookup[cellId]);
    // Persisted macro allocations own their FTE; ephemeral rows can be refreshed.
    return !existingCell || !!existingCell.lineItem?.isIntersectionOnly;
  });

/**
 * @param {any} lineItem
 */
const lineItemHasPersistedCellValue = (lineItem) =>
  (lineItem?.cells || []).some(
    (/** @type {any} */ cell) =>
      cell && cell.valueSaveStatus === SUCCESS && !isNil(cell.value)
  );

/**
 * Apply discovery FTE to cells on a row, keeping defaults in step when prefilling.
 * @param {any} lineItem
 * @param {Array<{ allocationProjectId: string, intersectingFte: number }>} targetRows
 * @param {boolean} prefillWithIntersectingFte
 * @returns {boolean} whether any cell changed
 */
const applyIntersectingFteToCells = (
  lineItem,
  targetRows,
  prefillWithIntersectingFte
) => {
  let changed = false;
  forEach(lineItem.cells, (cell) => {
    if (!cell) {
      return;
    }
    const matchingRow = targetRows.find(
      (row) => row.allocationProjectId === cell.allocationProjectId
    );
    if (!matchingRow) {
      return;
    }
    const nextSum = roundFte(matchingRow.intersectingFte);
    if (cell.currentFteSum !== nextSum) {
      cell.currentFteSum = nextSum;
      changed = true;
    }
    if (prefillWithIntersectingFte && cell.defaultedValue !== nextSum) {
      cell.defaultedValue = nextSum;
      changed = true;
    }
  });
  return changed;
};

/**
 * Add ephemeral supply line items for demand/source pairs that have intersecting
 * FTE but no persisted macro allocation. Behind enableForecastDemandViewIntersectionFixes.
 *
 * @param {any} state model slice
 * @param {{
 *   rows: Array<{
 *     allocationProjectId: string,
 *     sourceGroupId: string,
 *     targetGroupId: string,
 *     intersectingFte: number,
 *   }>,
 *   rootGroup: object,
 *   enableDemandRequest?: boolean,
 *   groupTypes?: Record<string, any>,
 * }} action
 */
const addEphemeralIntersectionSupplyLineItems = (state, action) => {
  const {
    dynamicModel,
    isBudgetEnabled,
    prefillWithIntersectingFte,
    hideHiddenTeams,
  } = state;
  if (!dynamicModel?.lookups) {
    return state;
  }

  const {
    rows,
    rootGroup,
    enableDemandRequest = false,
    groupTypes = {},
    groups = {},
  } = action;
  const { columns, lookups } = dynamicModel;
  const { groupingLookup, groupLookup } = lookups;
  // Copy so memos keyed on these lookups see new ephemeral rows.
  const cellLookup = { ...lookups.cellLookup };
  const lineItemLookup = { ...lookups.lineItemLookup };

  // Hydrate source groups onto the model lookup (lookupData is updated separately).
  Object.assign(groupLookup, groups || {});

  const intersectionOnlyRows = filterIntersectionOnlyRows(rows, cellLookup);
  const hasEphemeralLineItems = Object.values(lineItemLookup).some(
    (lineItem) => /** @type {any} */ (lineItem)?.isIntersectionOnly
  );
  if (!intersectionOnlyRows.length && !hasEphemeralLineItems) {
    return state;
  }

  /** @type {Map<string, typeof intersectionOnlyRows>} */
  const rowsByTargetAndSource = new Map();
  forEach(intersectionOnlyRows, (row) => {
    const sourceGroup = groupLookup[row.sourceGroupId];
    if (shouldHideForecastGroup(hideHiddenTeams, sourceGroup)) {
      return;
    }
    const key = `${row.targetGroupId}::${row.sourceGroupId}`;
    const existing = rowsByTargetAndSource.get(key);
    if (existing) {
      existing.push(row);
    } else {
      rowsByTargetAndSource.set(key, [row]);
    }
  });

  let didChange = false;
  const touchedGroupings = new Set();

  forEach(Object.values(lineItemLookup), (/** @type {any} */ lineItem) => {
    if (
      lineItem?.isIntersectionOnly &&
      lineItemHasPersistedCellValue(lineItem)
    ) {
      lineItem.isIntersectionOnly = false;
      didChange = true;
    }
  });

  forEach([...rowsByTargetAndSource.entries()], ([key, targetRows]) => {
    const [targetGroupId, sourceGroupId] = key.split("::");
    const demandGrouping = groupingLookup[targetGroupId];
    const sourceGroup = groupLookup[sourceGroupId];
    if (
      !demandGrouping ||
      !sourceGroup ||
      shouldHideForecastGroup(hideHiddenTeams, sourceGroup)
    ) {
      return;
    }

    const existingLineItem = demandGrouping.lineItems.find(
      (lineItem) => lineItem.groupId === sourceGroupId && !lineItem.type
    );
    if (existingLineItem) {
      if (
        existingLineItem.isIntersectionOnly &&
        applyIntersectingFteToCells(
          existingLineItem,
          targetRows,
          prefillWithIntersectingFte
        )
      ) {
        hashLineItem(existingLineItem, { rehashParents: true });
        touchedGroupings.add(demandGrouping);
        didChange = true;
      }
      return;
    }

    const groupType = get(groupTypes, sourceGroup.type, {});
    const lineItem = newLineItem({
      group: sourceGroup,
      grouping: demandGrouping,
      groupId: sourceGroupId,
      columnCount: columns.length,
      memberCount: sourceGroup.memberCount,
      canViewInDetails: true,
      canViewInTeamBuilder: true,
      canRename: !!groupType.canRename,
      canHide: false,
      isSupply: true,
      isSkillsMandatory: demandGrouping.isSkillsMandatory,
      dailyRate: getDailyRateByFlag(
        isBudgetEnabled,
        sourceGroup,
        demandGrouping,
        groupLookup
      ),
    });
    lineItem.isIntersectionOnly = true;

    setLineItemWithForecastDefaultCells({
      lineItem,
      targetGroupId,
      sourceGroupId,
      columns,
      rootGroup,
      prefillWithIntersectingFte,
      enableDemandRequest,
    });

    applyIntersectingFteToCells(
      lineItem,
      targetRows,
      prefillWithIntersectingFte
    );

    lineItem.isFteSumLoaded = true;
    demandGrouping.lineItems.push(lineItem);
    demandGrouping.lineItems.sort((a, b) =>
      (a.group?.name || "").localeCompare(b.group?.name || "")
    );

    hashLineItem(lineItem, { rehashParents: true });
    lineItemLookup[lineItem.id] = lineItem;
    forEach(lineItem.cells, (cell) => {
      if (cell?.id) {
        cellLookup[cell.id] = cell;
      }
    });

    touchedGroupings.add(demandGrouping);
    didChange = true;
  });

  // Discovery is the source of truth for ephemeral rows: zero periods it no
  // longer returns and drop rows whose whole pair has gone.
  forEach(Object.values(lineItemLookup), (/** @type {any} */ lineItem) => {
    if (!lineItem?.isIntersectionOnly) {
      return;
    }
    const demandGrouping = lineItem.grouping;
    const sourceGroup = groupLookup[lineItem.groupId];
    if (shouldHideForecastGroup(hideHiddenTeams, sourceGroup)) {
      if (demandGrouping?.lineItems) {
        demandGrouping.lineItems = demandGrouping.lineItems.filter(
          (/** @type {any} */ item) => item !== lineItem
        );
        hashLineItem(lineItem, { rehashParents: true });
        touchedGroupings.add(demandGrouping);
      }
      delete lineItemLookup[lineItem.id];
      forEach(lineItem.cells, (cell) => {
        if (cell?.id && cellLookup[cell.id] === cell) {
          delete cellLookup[cell.id];
        }
      });
      didChange = true;
      return;
    }

    const targetRows =
      rowsByTargetAndSource.get(
        `${demandGrouping?.groupId}::${lineItem.groupId}`
      ) || [];

    if (!targetRows.length) {
      if (demandGrouping?.lineItems) {
        demandGrouping.lineItems = demandGrouping.lineItems.filter(
          (/** @type {any} */ item) => item !== lineItem
        );
        hashLineItem(lineItem, { rehashParents: true });
        touchedGroupings.add(demandGrouping);
      }
      delete lineItemLookup[lineItem.id];
      forEach(lineItem.cells, (cell) => {
        if (cell?.id && cellLookup[cell.id] === cell) {
          delete cellLookup[cell.id];
        }
      });
      didChange = true;
      return;
    }

    let zeroed = false;
    forEach(lineItem.cells, (cell) => {
      if (
        !cell ||
        targetRows.some(
          (row) => row.allocationProjectId === cell.allocationProjectId
        )
      ) {
        return;
      }
      if (cell.currentFteSum !== 0) {
        cell.currentFteSum = 0;
        zeroed = true;
      }
      if (prefillWithIntersectingFte && cell.defaultedValue !== 0) {
        cell.defaultedValue = 0;
        zeroed = true;
      }
    });
    if (zeroed) {
      hashLineItem(lineItem, { rehashParents: true });
      if (demandGrouping) {
        touchedGroupings.add(demandGrouping);
      }
      didChange = true;
    }
  });

  if (!didChange) {
    return state;
  }

  // Recompute from each affected root so ancestors include ephemeral FTE.
  const rootsToUpdate = new Set();
  forEach([...touchedGroupings], (grouping) => {
    let root = grouping;
    while (root.parent) {
      root = root.parent;
    }
    rootsToUpdate.add(root);
  });

  forEach([...rootsToUpdate], (rootGrouping) => {
    setGroupingTotalFteSum(rootGrouping, columns, prefillWithIntersectingFte);
    if (prefillWithIntersectingFte) {
      setGroupingWithCalculatedSumTotals(rootGrouping, columns);
    }
  });

  return {
    ...state,
    dynamicModel: {
      ...dynamicModel,
      lookups: { ...lookups, cellLookup, lineItemLookup },
    },
  };
};

export default addEphemeralIntersectionSupplyLineItems;
